import { useCallback, useEffect, useRef, useState } from 'react';

import { useHistory, useParams } from 'react-router-dom';

import LocationAlertService from '@src/services/LocationAlertService';
import safeExecute from '@src/util/safeExecute';

import { MISSING_PARAMS_ERROR } from '../../../constants';
import type { UseMainAlertReturn } from '../../Alert/types';

interface MainAlertParams {
  orgId?: string;
  divId?: string;
  locationId?: string;
}

export const useMainAlert = (): UseMainAlertReturn => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const params = useParams<MainAlertParams>();
  const history = useHistory();
  const orgId = params.orgId ?? '';
  const divId = params.divId ?? '';
  const locationId = params.locationId ?? '';

  const serviceRef = useRef<LocationAlertService | null>(null);
  if (serviceRef.current === null) {
    serviceRef.current = new LocationAlertService();
  }

  const registerVisit = useCallback(async () => {
    if (!orgId || !divId || !locationId) {
      setError(new Error(MISSING_PARAMS_ERROR));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await safeExecute(
        async () => {
          await serviceRef.current!.registerVisit(orgId, divId, locationId);
        },
        'LocationAlertService.registerVisit',
        { orgId, divId, locationId },
      );
      const search = new URLSearchParams({ orgId, divId, locationId }).toString();
      history.replace({
        pathname: '/locationAlert',
        search: `?${search}`,
        state: { orgId, divId, locationId },
      });
    } catch (visitError) {
      setError(visitError instanceof Error ? visitError : new Error(String(visitError)));
    } finally {
      setLoading(false);
    }
  }, [divId, history, locationId, orgId]);

  useEffect(() => {
    registerVisit();
  }, [registerVisit]);

  return { error, loading };
};
